import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, startAfter, limit, getDocs
} from 'firebase/firestore';
import { firestore as db } from '../views/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import MessagesModal from '../components/MessagesModal';
import PostCard from '../components/PostCard';
import Spinner from '../components/Spinner';
import AuthDialog from '../components/AuthDialog';
import CreatePostModal from './CreatePostModal';
import PostManagerModal from './PostManagerModal';
import FavoritesModal from './FavoritesModal';
import SettingsModal from './SettingsModal';
import FeedMapSwitch from '../components/FeedMapSwitch';
import { Form, Button } from 'react-bootstrap';
import {
  FaFilter,
  FaPlus,
  FaFlagCheckered,
  FaBookmark,
  FaSlidersH,
  FaSearch,
  FaTimes,
  FaRedoAlt,
  FaCommentDots,
  FaCog,
  FaEllipsisH,
} from 'react-icons/fa';
import './FeedView.css';

const FEED_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'Pool/Billiards', label: 'Pool' },
  { key: 'Quick Race / Match', label: 'Race' },
  { key: 'Tournament', label: 'Tournaments' },
  { key: 'League Game', label: 'League' },
  { key: 'For Sale', label: 'Market' },
  { key: 'Other', label: 'Other' },
];

const toLower = (value) => String(value || '').toLowerCase();

export default function FeedView() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const feedListRef = useRef(null);

  const [searchOpen, setSearchOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  const [posts, setPosts] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);

  const [activeModal, setActiveModal] = useState(null);
  const [showMessages, setShowMessages] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem('feed-scroll-pos');
    if (saved && feedListRef.current) {
      feedListRef.current.scrollTop = parseInt(saved, 10);
    }
  }, []);

  const requireUser = useCallback((next) => {
    if (!user) {
      setShowLoginModal(true);
      return false;
    }
    next?.();
    return true;
  }, [user]);

  const handleScroll = () => {
    if (feedListRef.current) {
      sessionStorage.setItem('feed-scroll-pos', feedListRef.current.scrollTop);
    }
  };

  const resetFeed = useCallback(() => {
    setPosts([]);
    setLastDoc(null);
    setReachedEnd(false);
    setLoadingFeed(true);
    sessionStorage.removeItem('feed-scroll-pos');
    if (feedListRef.current) feedListRef.current.scrollTo({ top: 0 });
  }, []);

  const handleSearchToggle = () => {
    setSearchOpen(prev => !prev);
    setSearchText('');
    setActiveModal(null);
    setShowMessages(false);
  };

  const openMessages = () => requireUser(() => {
    setActiveModal(null);
    setShowMessages(true);
  });

  const closeMessages = () => setShowMessages(false);

  const fetchPosts = useCallback(async () => {
    if (fetchingMore || reachedEnd) return;
    setFetchingMore(true);

    try {
      const constraints = [
        where('status', '==', 'active'),
        orderBy('timestamp', 'desc'),
        limit(14),
      ];
      if (lastDoc) constraints.push(startAfter(lastDoc));

      const snap = await getDocs(query(collection(db, 'feedPost'), ...constraints));

      if (snap.empty) {
        setReachedEnd(true);
      } else {
        const newPosts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPosts(prev => {
          const combined = [...prev, ...newPosts];
          return Array.from(new Map(combined.map(p => [p.id, p])).values());
        });
        setLastDoc(snap.docs[snap.docs.length - 1]);
      }
    } catch (err) {
      console.error('Feed fetch failed:', err);
    } finally {
      setFetchingMore(false);
      setLoadingFeed(false);
    }
  }, [fetchingMore, reachedEnd, lastDoc]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    setSearching(true);
    const h = setTimeout(() => {
      setDebouncedSearch(searchText.trim().toLowerCase());
      setSearching(false);
    }, 220);
    return () => clearTimeout(h);
  }, [searchText]);

  const filtered = useMemo(() => posts.filter(p => {
    const matchesFilter = activeFilter === 'all' || p.category === activeFilter || toLower(p.category).includes(toLower(activeFilter));
    if (!matchesFilter) return false;

    if (!debouncedSearch) return true;
    const q = debouncedSearch;
    return (
      toLower(p.nickname).includes(q) ||
      toLower(p.postTitle).includes(q) ||
      toLower(p.postText).includes(q) ||
      toLower(p.category).includes(q) ||
      (Array.isArray(p.tags) && p.tags.some(tag => toLower(tag).includes(q)))
    );
  }), [posts, debouncedSearch, activeFilter]);

  const refreshFeed = () => {
    resetFeed();
    setTimeout(fetchPosts, 0);
  };

  if (authLoading) return null;

  return (
    <div className="feed-container p4r-feed-shell">
      <main className="p4r-feed-main">
        <aside className="p4r-feed-release-banner">
          <img src="/assets/pool4real-app-icon.png" alt="" />
          <div><strong>Pool 4 Real is official on iPhone and Android.</strong><span>Keep your profile, matches, teams, tournaments, and messages with you.</span></div>
          <Link to="/download">Get the apps</Link>
        </aside>

        {filtersOpen && (
          <div className="p4r-feed-filter-row" aria-label="Feed filters">
            {FEED_FILTERS.map(filter => (
              <button
                key={filter.key}
                className={activeFilter === filter.key ? 'active' : ''}
                onClick={() => {
                  setActiveFilter(filter.key);
                  setFiltersOpen(false);
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}

        {searchOpen && (
          <div className="feed-search-bar">
            <Form.Control
              type="text"
              autoFocus
              placeholder="Search posts, players, tags…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <Button
              variant="link"
              className="feed-search-close"
              onClick={handleSearchToggle}
              aria-label="Close search"
            >
              <FaTimes />
            </Button>
          </div>
        )}

        <div className="feed-list" ref={feedListRef} onScroll={handleScroll}>
          {searching || loadingFeed ? (
            <div className="feed-loading"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <div className="feed-empty">
              <strong>No posts found.</strong>
              <span>Try a different search or filter.</span>
            </div>
          ) : (
            filtered.map(post => (
              <PostCard
                key={post.id}
                post={post}
                onRequireAuth={() => setShowLoginModal(true)}
              />
            ))
          )}

          {!reachedEnd && filtered.length > 0 && (
            <div className="feed-loadmore">
              <Button
                variant="outline-primary"
                onClick={fetchPosts}
                disabled={fetchingMore}
              >
                {fetchingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      </main>


      <FeedMapSwitch showingMap={false} />

      <div className="p4r-feed-fab-dock" aria-label="Feed actions">
        {toolsOpen && (
          <div className="p4r-feed-fab-menu">
            <button type="button" onClick={handleSearchToggle} aria-label="Search feed" title="Search"><FaSearch /></button>
            <button type="button" onClick={() => setFiltersOpen(v => !v)} aria-label="Filter feed" title="Filters"><FaFilter /></button>
            <button type="button" onClick={refreshFeed} aria-label="Refresh feed" title="Refresh"><FaRedoAlt /></button>
            <button type="button" onClick={() => requireUser(() => navigate('/race-room'))} aria-label="Race room" title="Race room"><FaFlagCheckered /></button>
            <button type="button" onClick={() => requireUser(() => setActiveModal('favorites'))} aria-label="Favorites" title="Favorites"><FaBookmark /></button>
            <button type="button" onClick={() => requireUser(() => setActiveModal('postmanager'))} aria-label="Manage posts" title="Manage posts"><FaSlidersH /></button>
            <button type="button" onClick={openMessages} aria-label="Messages" title="Messages"><FaCommentDots /></button>
            <button type="button" onClick={() => requireUser(() => setActiveModal('settings'))} aria-label="Settings" title="Settings"><FaCog /></button>
          </div>
        )}

        <button
          type="button"
          className="p4r-feed-fab-secondary"
          onClick={() => setToolsOpen(v => !v)}
          aria-label="Feed tools"
          aria-expanded={toolsOpen}
        >
          <FaEllipsisH />
        </button>

        <Link
          to="/race-room"
          className="p4r-feed-fab-race"
          onClick={(event) => { if (!user) { event.preventDefault(); setShowLoginModal(true); } }}
          aria-label="Open Race Room"
          title="Start a race"
        >
          <FaFlagCheckered />
        </Link>
        <button
          type="button"
          className="p4r-feed-fab-primary"
          onClick={() => requireUser(() => setActiveModal('addpost'))}
          aria-label="Create post"
          title="Create Pockit"
        >
          <FaPlus />
        </button>
      </div>

      {activeModal === 'addpost' && <CreatePostModal onClose={() => setActiveModal(null)} onCreated={resetFeed} />}
      {activeModal === 'postmanager' && (
        <PostManagerModal
          onClose={() => setActiveModal(null)}
          onCreate={() => setActiveModal('addpost')}
        />
      )}
      {activeModal === 'favorites' && <FavoritesModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'settings' && <SettingsModal show onClose={() => setActiveModal(null)} />}
      <MessagesModal show={showMessages} onClose={closeMessages} />

      <AuthDialog
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        subtitle="Sign in to create Pockits, react, save posts, and message players."
      />
    </div>
  );
}
